import React, { Component, useState } from 'react';
import StackedContent from "@components/StackedContent/StackedContent";
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import Accordion from "@components/Accordion/Accordion";
import Card from "@/components/Card/Card";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import PageNavigation from "@/components/PageNavigation/PageNavigation";
import StatusMarker from '@components/StatusMarker/StatusMarker';
import { useLinksContext } from '@context/LinksContext';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useTasklist } from '@/context/TasklistContext';
import { getTasklistId, getTasklistNavigationMeta } from '@/utils/tasklistLegacyNavigation';
import { openChat } from '@/utils/linksUtils';
import setupAccounts from "@assets/icons/setup-accounts.webp";
import olsAccounts from "@assets/icons/ols-account.png";
import mailbox from "@assets/icons/tip-email.png";
import { useNavigate } from 'react-router-dom';

const StudentAccountSetUp = () => {
    const { myAccountLink } = useLinksContext();
    const { userRoleVal, identityId, students, loggedInUser = null, loading, error } = useOverviewContext();

    // Open chat
    const handleChatOnClick = () => {
        if (!identityId) {
            console.warn('Chat not available: identityId is missing');
            return;
        }
        openChat(identityId);
    };

    // checklist
    const { selectedStudent } = useSelectedStudent();
    const { tasklistData, tasklistTopicStatus } = useTasklist();
    const checklistId = getTasklistId(tasklistData, 'StudentAccountSetUp');
    const isReadyToRender =
        selectedStudent?.accountId !== undefined &&
        selectedStudent?.accountId !== null &&
        typeof checklistId === 'number';
    
    // Page Navigation Feature
    const navigate = useNavigate();
    const {
    currentIndex,
    totalTasks,
    previousSubtitle,
    nextSubtitle,
    nextSlug,
    prevSlug,
    } = getTasklistNavigationMeta(tasklistData, window.location.pathname, selectedStudent?.schoolId);
    
    const handleNext = () => {
        if (nextSlug) {
            navigate(`/tasklist/${nextSlug}`);
        }
    };  
    const handlePrevious = () => {
        if (prevSlug) {
            navigate(`/tasklist/${prevSlug}`);
        }
    };

    const breadcrumbItems = [
        { title: "Home", path: "/" },
        { title: 'School prep tasklist', path: '/tasklist' },
        { title: "Set Up Your Accounts" }
    ];

    // Handle myaccount
    const getAccountLink = () => {
        try {
        if (!myAccountLink) {
            console.warn("My Account link is missing.");
            return;
        }
        const cleanUrl = myAccountLink.trim().replace(/^"|"$/g, "");
        window.open(cleanUrl, "_blank");
        } catch (error) {
        console.error("Failed to open My Account link:", error);
        }
    };

    return (
        <>
        <div className="setup-your-accounts">
            <div className="sub-page-hero">
                <Breadcrumb items={breadcrumbItems} />
                <SubPageHero
                    theme="primary"
                    title="Set Up Your Accounts"
                    description="Make sure your accounts are set up for a smooth start to the school year."
                    image={setupAccounts}
                />
            </div>
            {loggedInUser?.role === 'LEARNING_COACH' && (
                <StackedContent>
                    <StackedContent.Col1 cols="10">
                        <h2 className='heading-2'>Set Up Your Student's K12 School (OLS) Account</h2>
                        <div className='content-block'>
                            <p>K12 School (OLS) accounts are used to access this welcome site and the K12 School (OLS), where you and your student will go to log in at the start of each school day.</p>
                            <p>If you haven't already set up your student's K12 School (OLS) account, set it up now so they can get into class on the first day. Note: You may have already set up your student's account when you set up the account you created to access this site.</p>
                            <ol className='list-block'>
                                <li>Look for an email from <b>registration@k12.com</b> titled: <span>Welcome! Access Important School Account Info Here!</span></li>
                                <li>Find the Registration ID in the email. Copy it to your clipboard or write it down.</li>
                                <li>Go to <a href="https://registration.k12.com/" target='_blank'><b>registration.k12.com</b></a> and enter the Registration ID as it is shown in your email. Important! Use only capital letters and numbers. Add the hyphen without spaces.</li>
                                <li>Follow the instructions on the page to set up your student's account. Note: If you have more than one student account to set up, you will see a list of your students' names.</li>
                            </ol>
                            <p className='contact-info'>If you need help finding your Registration ID, please <span onClick={handleChatOnClick}>contact our customer support team.</span></p>
                        </div>
                    </StackedContent.Col1>
                </StackedContent>
            )}

            {loggedInUser?.role === 'LEARNING_COACH' && (
                <StackedContent>
                    <StackedContent.Col1 cols="10">
                        <Card
                        theme="primary-light"
                        orientation="horizontal"
                        eyebrow="Good to know"
                        header="Learning Coach vs. Student K12 School (OLS) Accounts"
                        description="From your Learning Coach account, you can view your student's courses, monitor their assignments and grades, see course content, and contact your student’s teachers. Your student will log in to their account to see their schedule, go to class, and complete their assignments."
                        buttonText=""
                        media={olsAccounts}
                    />
                    </StackedContent.Col1>
                </StackedContent>
            )}

            <StackedContent>
                <StackedContent.Col1 cols="10">
                    <h2 className='heading-2 '>Set Up Your Email Accounts</h2>

                {loggedInUser?.role === 'LEARNING_COACH' && (
                    <>
                    <div className='content-block'>
                        <p>Email is the primary method of communication between families and their school. Here's what you need to know about how school email works.</p>
                    </div>
                    <h3 className='heading-3 mt-2 pt-4'>Setting Up Your Learning Coach Email</h3>
                    <div className='content-block'>
                        <p><b>Good news—you can use your already existing email for school communications.</b></p>
                        <p>You will automatically receive copies of all messages sent to your student at the email address you provided on your enrollment application.</p>
                        <p>Learning Coaches can also use the "Contact Teachers" feature within their OLS account to send messages to teachers and school staff. When you use this email pop-up, you'll automatically receive a copy of the message you sent in your personal email inbox.</p>
                    </div>
                    </>
                )}

                {loggedInUser?.role === 'LEARNING_COACH' && (
                    <h2 className='heading-3 mt-2 pt-4'>Setting Up Your Student Email</h2>
                )}
                    <div className='content-block'>
                        <p>Students are automatically assigned a school email address powered by Office 365. To access student email, first complete this one-time set-up process:</p>
                        <ol className='list-block'>
                            <li>Find your school email address on the <a href="#" onClick={getAccountLink}><b>My Account</b></a> page</li>
                            <li>Go to <a href="https://office.com/" target='_blank'><b>https://office.com</b></a></li>
                            <li>Sign in using your school email address and password.</li>
                        </ol>
                    </div>
                    <div className='content-block'>
                        <p>Once you've completed this set-up process, you can access school email in the K12 School (OLS).</p>
                        <p>You can send messages by using the “Inbox” feature within the K12 School (OLS). Note: School email is limited to communicating with teachers and students. Messages will not be delivered to other students, friends, or any outside email address.</p>
                    </div>
                </StackedContent.Col1>
            </StackedContent>

            <StackedContent>
                <StackedContent.Col1 cols="10">
                    <Card
                      theme="primary-light"
                      orientation="horizontal"
                      eyebrow="Good to know"
                      header="Email Every Day"
                      description="Since most school communication will come through your email, it's important to read and respond to messages promptly. Form a good habit early by checking for new email every day."
                      buttonText=""
                      media={mailbox}
                  />
                </StackedContent.Col1>
            </StackedContent>

            <StackedContent>
                <StackedContent.Col1 cols="10">
                    <h2 className='heading-2'>Ways to Check Student Email</h2>
                    <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>Access through the OLS on any device (recommended)</Accordion.Header>
                            <Accordion.Body>
                                <p>Click the Inbox icon on the left of your screen to go to your inbox, create a new message, or contact a teacher.</p>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>Install Office 365 on your personal computer</Accordion.Header>
                            <Accordion.Body>
                                <p>Students can view mail and use additional tools within Office 365. Office 365 comes preinstalled on school-issued computers, but if you need to download it onto a personal computer, here's how:</p>
                                <ol className='faq-list'>
                                    <li>Go to the Microsoft Office website at <b><a href="https://office.com/">https://office.com</a></b></li>
                                    <li>Click the Sign In button and use your full email address and password to access your O365 account.</li>
                                    <li>Select Office 365 apps from the "Install Office" drop-down.</li>
                                    <li>You will then install the application from the download folder in your browser.</li>
                                    <li>Once the installation is complete, close the dialog box and launch the O365 application from the Windows Start Menu or the Applications Menu (if using a Mac).</li>
                                    <li>After the application opens, make sure you are signed into your account. You can sign in using your K12 School (OLS) username and password.</li>
                                </ol>
                                <div className='content-block pt-4'>
                                    <p>Need to locate or update your account info? Visit the <a href="#" onClick={getAccountLink}><b>My Account</b></a> page.</p>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>Download the Outlook app on your phone</Accordion.Header>
                            <Accordion.Body>
                                <p>If students have a smartphone and wish to receive school email messages with the Outlook application, here are the setup instructions:</p>
                                <p className='fb-700 mt-4'>iOS Instructions</p>
                                <ol className='faq-list'>
                                    <li>Find your email address on the <a href="#" onClick={getAccountLink}><b>My Account</b></a> page. Be sure to write it down or copy it to your clipboard.</li>
                                    <li>Download the Outlook for iOS application from the Apple Store</li>
                                    <li>Once installed, open up the application and tap Settings &#62; Add Account</li>
                                    <li>On the "Add Account" page, tap Office 365 and sign into your account using your full email address and your K12 School (OLS) password</li>
                                </ol>
                                <p className='fb-700 mt-4'>Android Instructions</p>
                                <ol className='faq-list'>
                                    <li>Find your email address on the <a href="#" onClick={getAccountLink}><b>My Account</b></a> page. Be sure to write it down or copy it to your clipboard.</li>
                                    <li>Download the Outlook for Android application from the Google Play store</li>
                                    <li>Once installed, open up the application and tap More &#62; Settings &#62; Add Account</li>
                                    <li>On the "Add Account" page, tap Office 365 and sign into your account using your full email address and your K12 School (OLS) password</li>
                                </ol>
                                <div className='content-block pt-4'>
                                    <p>Need to locate or update your account info? Visit the <a href="#" onClick={getAccountLink}><b>My Account</b></a> page.</p>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </StackedContent.Col1>
            </StackedContent>

            <StackedContent>
                <StackedContent.Col1 cols="10">
                    {isReadyToRender && (
                        <StatusMarker
                            accountId={selectedStudent!.accountId}
                            checklistId={checklistId}
                        />
                    )}
                </StackedContent.Col1>
            </StackedContent>

            <section className="navigation">
                <StackedContent>
                    <StackedContent.Col1 cols="10">
                        <PageNavigation
                        currentIndex={currentIndex}
                        totalTasks={totalTasks}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        previousLabel="Previous Task"
                        nextLabel="Next Task"
                        previousSubtitle={previousSubtitle}
                        nextSubtitle={nextSubtitle}
                    />               
                    </StackedContent.Col1>
                </StackedContent>
            </section>

        </div>
        </>
    );
};

export default StudentAccountSetUp;
